import { LegacyTable, type LegacyTableColumn } from '@ctw/shared/components/tables/legacy-table';
import { ReactNodeRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/react/react-node-renderer';
import { DOCUMENT_VIEWER_TABLE_TEST_ID } from '@ctw/shared/test/ids';
import {
  emptyTableMessage,
  filterChildNodesByNodeName,
  tableCellStylesFromContent,
  tableColumnStylesFromContent,
  textContentFromNodeTree,
  uniqueIdentifierForNode,
} from '@ctw/shared/utils/cda/helpers';
import type { CdaParsedHtmlElementNode } from '@ctw/shared/utils/cda/types';
import { tw } from '@ctw/shared/utils/tailwind';
import { compact, isEmpty, snakeCase } from 'lodash-es';
import { memo, useMemo } from 'react';

interface TableRendererProps {
  id: string;
  table: CdaParsedHtmlElementNode;
}

interface TableRecord {
  key: string;
  tdElements: Array<CdaParsedHtmlElementNode>;
}

interface TableDataFromHtml {
  caption: string;
  headersAreEffectivelyEmpty: boolean;
  cellsAreEffectivelyEmpty: boolean;
  columns: Array<LegacyTableColumn<TableRecord>>;
  records: Array<TableRecord>;
  rowHeaders: Array<string> | undefined;
}

export const tableDataFromHtml = (
  id: string,
  table: CdaParsedHtmlElementNode,
): TableDataFromHtml => {
  const captionElement = table.childNodes.find(
    (child) => child.nodeType === 'element' && child.nodeName === 'caption',
  );
  const captionElementText = captionElement ? textContentFromNodeTree(captionElement) : '';
  const caption = isEmpty(captionElementText) ? 'Untitled Table' : captionElementText;

  const theadElement: CdaParsedHtmlElementNode | undefined = table.childNodes.find(
    (child) => child.nodeType === 'element' && child.nodeName === 'thead',
  ) as CdaParsedHtmlElementNode | undefined;
  const tbodyElement: CdaParsedHtmlElementNode | undefined = table.childNodes.find(
    (child) => child.nodeType === 'element' && child.nodeName === 'tbody',
  ) as CdaParsedHtmlElementNode | undefined;

  const candidateHeaderRows = theadElement ? theadElement.childNodes : table.childNodes;
  const headerRow = candidateHeaderRows.find(
    (row) =>
      row.nodeType === 'element' &&
      row.nodeName === 'tr' &&
      !row.childNodes.some((child) => child.nodeType === 'element' && child.nodeName === 'td'),
  ) as CdaParsedHtmlElementNode | undefined;
  const columnThElements: Array<CdaParsedHtmlElementNode> = headerRow
    ? filterChildNodesByNodeName(headerRow, ['th'], false)
    : [];

  const rowThElements: Array<CdaParsedHtmlElementNode> = [];
  const candidateRowsWithHeaders = tbodyElement ? tbodyElement.childNodes : table.childNodes;
  for (const row of candidateRowsWithHeaders) {
    if (
      row.nodeType === 'element' &&
      row.nodeName === 'tr' &&
      row.childNodes.some((child) => child.nodeType === 'element' && child.nodeName === 'td')
    ) {
      const rowHeader = row.childNodes.find(
        (cell) => cell.nodeType === 'element' && cell.nodeName === 'th',
      ) as CdaParsedHtmlElementNode | undefined;

      if (rowHeader) {
        rowThElements.push(rowHeader);
      }
    }
  }

  const candidateRecordRows = tbodyElement ? tbodyElement.childNodes : table.childNodes;
  const recordTrElements = candidateRecordRows
    // We only support one column header row right now, so pick the one with the most header cells
    // which is typically the _real_ headers, as opposed to rows with fewer headers which are typically
    // rows abused as table headings
    .sort(
      (aRow, bRow) =>
        filterChildNodesByNodeName(bRow, ['th'], false).length -
        filterChildNodesByNodeName(aRow, ['th'], false).length,
    )
    .filter(
      (row) =>
        row.nodeType === 'element' &&
        row.nodeName === 'tr' &&
        row.childNodes.some((child) => child.nodeType === 'element' && child.nodeName === 'td'),
    );

  const rowHeaders = rowThElements.map((th) => textContentFromNodeTree(th).trim());

  const records: Array<TableRecord> = recordTrElements.map((tr, index) => ({
    key: uniqueIdentifierForNode({ node: tr, context: id, index, purpose: 'table-row' }),
    tdElements: filterChildNodesByNodeName(tr, ['td'], false),
  }));

  const cellsAreEffectivelyEmpty = records.every((record) =>
    record.tdElements.every((td) => td.isEffectivelyEmpty),
  );

  const mostTdsInARow = records.reduce(
    (tally, record) => (record.tdElements.length > tally ? record.tdElements.length : tally),
    0,
  );
  const paddedColumnHeaders = mostTdsInARow - columnThElements.length;
  const columnHeaders: Array<string> = [
    ...columnThElements.map((th) => textContentFromNodeTree(th).trim()),
    ...(paddedColumnHeaders > 0 ? new Array(paddedColumnHeaders).fill('') : []),
  ];

  const headersAreEffectivelyEmpty = !columnHeaders.some((header) => header.trim() !== '');

  const columns: Array<LegacyTableColumn<TableRecord>> = columnHeaders.map(
    (columnHeader, columnHeaderIndex) => {
      const { minWidth, width, maxWidth, className } = tableColumnStylesFromContent({
        columnCount: columnHeaders.length,
        columnTdElements: compact(records.map((record) => record.tdElements.at(columnHeaderIndex))),
      });
      return {
        minWidth,
        width,
        maxWidth,
        className,
        title: (
          <TableHeader
            id={id}
            text={columnHeader}
            searchIndex={columnThElements.at(columnHeaderIndex)?.searchIndexId ?? undefined}
          />
        ),
        headerClassName: `whitespace-nowrap${columnThElements.length > columnHeaderIndex ? ` ${columnThElements.at(columnHeaderIndex)?.styles.className ?? ''}` : ''}`,
        renderClassName: (record) =>
          [
            record.tdElements.length > columnHeaderIndex
              ? (record.tdElements.at(columnHeaderIndex)?.styles.className ?? '')
              : '',
            tableCellStylesFromContent({
              cellElement: record.tdElements.at(columnHeaderIndex),
            }).className,
          ].join(' '),
        render: (record) => {
          const td = record.tdElements.at(columnHeaderIndex);

          if (!td || isEmpty(textContentFromNodeTree(td).trim())) {
            return <span className={tw`text-content-subtle/50`}>&mdash;</span>;
          }

          const tableCellIdentifier = uniqueIdentifierForNode({
            node: td,
            context: `${id}-${snakeCase(columnHeader)}-${record.key}`,
            purpose: 'table-cell',
            index: columnHeaderIndex,
          });

          return (
            <>
              {(td?.childNodes ?? []).map((child, childIndex) => {
                const tableCellChildIdentifier = uniqueIdentifierForNode({
                  node: child,
                  context: tableCellIdentifier,
                  purpose: 'table-cell-child',
                  index: childIndex,
                });

                return (
                  <ReactNodeRenderer
                    id={tableCellChildIdentifier}
                    key={tableCellChildIdentifier}
                    node={child}
                  />
                );
              })}
            </>
          );
        },
      };
    },
  );

  return {
    caption,
    headersAreEffectivelyEmpty,
    cellsAreEffectivelyEmpty,
    columns,
    records,
    rowHeaders,
  };
};

export const ReactTableRenderer = memo(({ id, table }: TableRendererProps) => {
  const {
    caption,
    headersAreEffectivelyEmpty,
    cellsAreEffectivelyEmpty,
    columns,
    records,
    rowHeaders,
  } =
    // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally under-specific deps
    useMemo(() => tableDataFromHtml(id, table), [id]);

  return (
    <LegacyTable
      key={id}
      id={id}
      testId={DOCUMENT_VIEWER_TABLE_TEST_ID}
      title={caption}
      titleVisuallyHidden={true}
      className={tw`!border w-full max-w-full rounded-md border-background-subtle`}
      containerClassName={tw`cda-renderer-table-container`}
      showTableHead={!headersAreEffectivelyEmpty}
      records={cellsAreEffectivelyEmpty ? [] : records}
      columns={columns}
      rowHeaders={rowHeaders}
      spacing="tight"
      emptyMessage={emptyTableMessage}
      stackedColumnHeaders={true}
      emptyStateShowHeaders={true}
    />
  );
});

interface TableHeaderProps {
  id: string;
  text: string;
  searchIndex?: string;
}

const TableHeader = memo(({ id, text, searchIndex }: TableHeaderProps) => {
  return (
    <span
      key={`${id}-table-header-${snakeCase(text)}`}
      data-search-index={searchIndex}
      className="whitespace-nowrap"
    >
      {text}
    </span>
  );
});
